import type {
  FleetFuelCardStatus,
  FleetFuelIssuer,
  FleetFuelMatchStatus,
  FleetFuelProduct,
  FleetFuelType,
  FleetInspectionKind,
  FleetInspectionResult,
  FleetOperationalStatus,
  FleetOdometerSource,
  FleetOwnership,
  FleetUsageMode,
  FleetVehicleCategory,
} from '@gb-ticket/shared-types';

export const FLEET_CATEGORY_LABELS: Record<FleetVehicleCategory, string> = {
  car: 'Pkw',
  van: 'Transporter',
  truck: 'Lkw',
  trailer: 'Anhänger',
  other: 'Sonstiges',
};

export const FLEET_FUEL_ISSUER_LABELS: Record<FleetFuelIssuer, string> = {
  dkv: 'DKV',
  uta: 'UTA',
  shell: 'Shell',
  aral: 'Aral',
  other: 'Sonstiger Aussteller',
};

export const FLEET_FUEL_CARD_STATUS_LABELS: Record<FleetFuelCardStatus, string> = {
  active: 'Aktiv',
  blocked: 'Gesperrt',
  lost: 'Verloren',
  returned: 'Zurückgegeben',
};

export const FLEET_FUEL_MATCH_STATUS_LABELS: Record<FleetFuelMatchStatus, string> = {
  matched: 'Zugeordnet',
  exception: 'Ausnahme',
};

export const FLEET_FUEL_PRODUCT_LABELS: Record<FleetFuelProduct, string> = {
  diesel: 'Diesel',
  petrol: 'Benzin',
  adblue: 'AdBlue',
  charge: 'Strom',
  other: 'Sonstiges',
};

export const FLEET_FUEL_LABELS: Record<FleetFuelType, string> = {
  petrol: 'Benzin',
  diesel: 'Diesel',
  electric: 'Elektrisch',
  hybrid: 'Hybrid',
  other: 'Sonstiges',
};

export const FLEET_OWNERSHIP_LABELS: Record<FleetOwnership, string> = {
  owned: 'Eigentum',
  leased: 'Leasing',
};

export const FLEET_USAGE_LABELS: Record<FleetUsageMode, string> = {
  assigned: 'Fest zugeordnet',
  pool: 'Pool',
};

export const FLEET_STATUS_LABELS: Record<FleetOperationalStatus, string> = {
  in_service: 'Einsatzbereit',
  out_of_service: 'Nicht fahrbereit',
  in_workshop: 'In der Werkstatt',
  retired: 'Stillgelegt',
};

export const FLEET_INSPECTION_KIND_LABELS: Record<FleetInspectionKind, string> = {
  hu: 'Hauptuntersuchung',
  uvv_vehicle: 'UVV-Prüfung',
};

export const FLEET_INSPECTION_RESULT_LABELS: Record<FleetInspectionResult, string> = {
  passed: 'Bestanden',
  failed: 'Nicht bestanden',
};

export const FLEET_ODOMETER_SOURCE_LABELS: Record<FleetOdometerSource, string> = {
  driver: 'Fahrer',
  workshop: 'Werkstatt',
  correction: 'Korrektur',
  telematics: 'Telematik',
  fuel_card: 'Tankkarte',
};

export const FLEET_DAMAGE_KIND_LABELS = {
  damage: 'Schaden',
  defect: 'Mangel',
} as const;

export const FLEET_DAMAGE_SEVERITY_LABELS = {
  drivable: 'Fahrbereit',
  not_drivable: 'Nicht fahrbereit',
} as const;

export const FLEET_DAMAGE_STATUS_LABELS = {
  reported: 'Gemeldet',
  triaged: 'In Prüfung',
  in_workshop: 'In der Werkstatt',
  done: 'Erledigt',
  rejected: 'Zurückgewiesen',
} as const;

export const FLEET_DAMAGE_EVENT_LABELS = {
  reported: 'Gemeldet',
  triaged: 'In Prüfung',
  in_workshop: 'In der Werkstatt',
  done: 'Erledigt',
  rejected: 'Zurückgewiesen',
  vehicle_held: 'Fahrzeug gesperrt',
  vehicle_released: 'Fahrzeug freigegeben',
} as const;

export function formatDeDate(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
}

/** Date-only comparison in the local calendar. Today is not overdue. */
export function isFleetDateOverdue(iso: string | null | undefined, now = new Date()): boolean {
  if (!iso) {
    return false;
  }
  const day = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    return false;
  }
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return day < today;
}

export function formatFleetDueDate(iso: string | null | undefined, now = new Date()): string {
  if (!iso) {
    return '—';
  }
  const label = formatDeDate(iso);
  return isFleetDateOverdue(iso, now) ? `${label} · überfällig` : label;
}

export const FLEET_WORKSHOP_KIND_LABELS = {
  service: 'Service',
  repair: 'Reparatur',
  inspection: 'Prüfung',
  other: 'Sonstiges',
} as const;

export const FLEET_DOCUMENT_KIND_LABELS = {
  registration: 'Zulassung',
  leasing: 'Leasing',
  insurance: 'Versicherung',
  inspection_report: 'Prüfbericht',
  invoice: 'Rechnung',
  damage_photo: 'Schadenfoto',
  license_evidence: 'Führerschein-Foto',
  other: 'Sonstiges',
} as const;

export const FLEET_TRIP_PURPOSE_LABELS = {
  unset: 'Offen',
  business: 'Dienstlich',
  private: 'Privat',
} as const;

export const FLEET_TELEMATICS_ISSUE_LABELS: Record<string, string> = {
  unknown_vehicle: 'Fahrzeug unbekannt',
  coordinates_invalid: 'Koordinaten ungültig',
  odometer_decrease: 'Kilometerstand sinkt',
};

export function formatDeDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
